import { AfterViewInit, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AiWatchBoardChange } from '../../../../component/ai-price-watch-widget/ai-price-watch-widget.component';
import { NAVIGATIONS, URLS } from '../../../../data/navigation/navigation.data';
import { AI_TOKEN_METER_MORE_TOOL } from '../../../../data/product/ai-token-meter-features.data';
import { AI_PRICE_WATCH_ABOUT } from '../../../../data/tool/tool-about.data';
import { AnalyticsService } from '../../../../service/analytics/analytics.service';
import { AiPrices, AiPricesService } from '../../../../service/ai/ai-prices.service';
import { AiChanges, AiIndexRow, AiPriceEvent, AiWatchService } from '../../../../service/ai/ai-watch.service';
import { SeoService } from '../../../../service/seo/seo.service';
import { aiPlatformIcon } from '../../../../util/ai-cost';
import {
  AI_WATCH_DEFAULT_FIELD, AI_WATCH_DEFAULT_RANGE, AI_WATCH_FIELDS, AI_WATCH_RANGES, aiWatchDays, aiWatchOrder, aiWatchPct,
  aiWatchPctLabel, aiWatchRangeDays, AiWatchField, AiWatchRange,
} from '../../../../util/ai-price-watch';

/** one row of Compare Every Model: a model's picked price and its change over each range */
interface CompareRow {
  id: string;
  name: string;
  vendor: string;
  platform: string;
  input: number;
  output: number;
  price: number | null;
  changes: ( number | null )[];
}

/** a column of the compare table to sort by: the name, the platform, the price or one of the ranges (0..4) */
type SortKey = 'name' | 'platform' | 'price' | number;

/**
 * /tools/ai-price-watch (owner 2026-10-28, "borsa hisseleri gibi"): a stock board of AI token prices. The board
 * (app-ai-price-watch-widget, also in the home's tools box) shows the tickers, the picked model's price line and
 * the tape; Compare Every Model lists every model with its change over each range. ?model=<id>&field=input&range=1y&q=
 * opens the board on that model and every change writes back to the url. The data is GET /ai/prices, /ai/changes
 * and /ai/index; a price line is rebuilt from today's price and the moves.
 */
@Component( {
  selector: 'app-ai-price-watch-page',
  templateUrl: './ai-price-watch-page.component.html',
  styleUrls: [
    './ai-price-watch-page.component.scss',
    '../ai-cost-page/ai-cost-table.scss',
    '../../tool-pages/wallpaper-page/wallpaper-page.component.scss',
    '../../tool-pages/tools-page/tools-page.component.scss',
    '../../plugin-pages/plugins-page/plugins-page.component.scss',
  ],
} )
export class AiPriceWatchPageComponent implements OnInit, AfterViewInit, OnDestroy {

  URLS = URLS;
  readonly ranges = AI_WATCH_RANGES;

  changes: AiChanges | null = null;
  prices: AiPrices | null = null;
  indexRows: AiIndexRow[] = [];

  // the board's state, from the url and back to it
  field: AiWatchField = AI_WATCH_DEFAULT_FIELD;
  range: AiWatchRange = AI_WATCH_DEFAULT_RANGE;
  model = '';
  query = '';

  // Compare Every Model
  compareRows: CompareRow[] = [];
  platformKeys: string[] = [];
  tablePlatform = 'all';
  tableQuery = '';
  sortKey: SortKey = 'platform';
  sortDesc = false;
  private compareAll: CompareRow[] = [];

  pageSections: { key: string; label: string }[] = [
    { key: 'compare', label: 'Compare Every Model' },
    { key: 'more', label: 'More AI Tools' },
    { key: 'about', label: 'How It Works' },
    { key: 'reviews', label: 'Reviews' },
  ];
  activeSection = 'top';
  readonly aiTokenMeter = AI_TOKEN_METER_MORE_TOOL;
  readonly aboutLead = AI_PRICE_WATCH_ABOUT.lead;
  readonly aboutParagraphs = AI_PRICE_WATCH_ABOUT.paragraphs;
  readonly faq = AI_PRICE_WATCH_ABOUT.faq;

  private sectionObserver?: IntersectionObserver;
  private subs: Subscription[] = [];
  private loggedFilter = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private watch: AiWatchService,
    private priceService: AiPricesService,
    private analyticsService: AnalyticsService,
    private seoService: SeoService,
    private zone: NgZone,
  ) {
  }

  ngOnInit(): void {
    this.seoService.setMetaData( {
      title: NAVIGATIONS.aiPriceWatch.title,
      description: NAVIGATIONS.aiPriceWatch.description,
      keywords: NAVIGATIONS.aiPriceWatch.keywords,
      robots: [ 'index', 'follow', 'max-image-preview:large', 'max-snippet:-1' ],
    } );
    this.subs.push( this.route.queryParams.subscribe( q => {
      const field = String( q.field || AI_WATCH_DEFAULT_FIELD ) as AiWatchField;
      this.field = AI_WATCH_FIELDS.some( f => f.key === field ) ? field : AI_WATCH_DEFAULT_FIELD;
      const range = String( q.range || AI_WATCH_DEFAULT_RANGE ) as AiWatchRange;
      this.range = AI_WATCH_RANGES.some( r => r.key === range ) ? range : AI_WATCH_DEFAULT_RANGE;
      this.model = typeof q.model === 'string' ? q.model : '';
      this.query = typeof q.q === 'string' ? q.q : '';
      this.buildCompare();
    } ) );
    this.subs.push( this.watch.changes$.subscribe( data => {
      this.changes = data;
      this.buildCompare();
    } ) );
    this.subs.push( this.priceService.prices$.subscribe( data => {
      this.prices = data;
      this.buildCompare();
    } ) );
    this.subs.push( this.watch.index$.subscribe( data => {
      this.indexRows = data.days;
      this.buildCompare();
    } ) );
  }

  ngAfterViewInit(): void {
    if ( typeof IntersectionObserver === 'undefined' ) { return; }
    this.sectionObserver = new IntersectionObserver( entries => {
      for ( const entry of entries ) {
        if ( entry.isIntersecting ) {
          const sec = ( entry.target as HTMLElement ).dataset[ 'section' ];
          if ( sec && sec !== this.activeSection ) { this.zone.run( () => ( this.activeSection = sec ) ); }
        }
      }
    }, { rootMargin: '-45% 0px -50% 0px', threshold: 0 } );
    setTimeout( () => document.querySelectorAll( '[data-section]' ).forEach( el => this.sectionObserver!.observe( el ) ) );
  }

  ngOnDestroy(): void {
    this.subs.forEach( s => s.unsubscribe() );
    this.sectionObserver?.disconnect();
  }

  // ---------------------------------------------------------------- words and numbers

  platformLabel( key: string ): string {
    return this.changes?.platforms[ key ]?.label || this.prices?.platforms[ key ]?.label || key;
  }

  platformIcon( key: string ): string {
    return aiPlatformIcon( key );
  }

  price( value: number | null ): string {
    return AiPricesService.price( value );
  }

  pctLabel( pct: number | null ): string {
    return aiWatchPctLabel( pct );
  }

  get fieldLabel(): string {
    return ( AI_WATCH_FIELDS.find( f => f.key === this.field ) || AI_WATCH_FIELDS[ 0 ] ).label;
  }

  get fieldName(): string {
    return this.fieldLabel.toLowerCase();
  }

  // ---------------------------------------------------------------- the board

  /** the board moved: its state into the url, the table on the same price */
  onBoard( change: AiWatchBoardChange ): void {
    const fieldChanged = change.field !== this.field;
    this.model = change.model;
    this.field = change.field;
    this.range = change.range;
    this.query = change.query;
    if ( fieldChanged ) { this.buildCompare(); }
    this.router.navigate( [], {
      relativeTo: this.route,
      queryParams: {
        field: this.field === AI_WATCH_DEFAULT_FIELD ? null : this.field,
        range: this.range === AI_WATCH_DEFAULT_RANGE ? null : this.range,
        model: this.model || null,
        q: this.query.trim() || null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    } );
    if ( !this.loggedFilter || change.source !== 'search' ) {
      this.loggedFilter = true;
      this.analyticsService.logEvent( 'AI Price Watch Filtered', { source: change.source, field: this.field, range: this.range, model: this.model, query: this.query.trim() } );
    }
  }

  // ---------------------------------------------------------------- Compare Every Model

  setTablePlatform( key: string ): void {
    this.tablePlatform = key;
    this.sortTable();
  }

  setTableQuery( value: string ): void {
    this.tableQuery = value;
    this.sortTable();
  }

  /** a column head: sorts by it, a second click turns the order around */
  sortBy( key: SortKey ): void {
    if ( this.sortKey === key ) {
      this.sortDesc = !this.sortDesc;
    } else {
      this.sortKey = key;
      this.sortDesc = typeof key === 'number';
    }
    this.sortTable();
  }

  /** a row of the table: that model on the board, the board scrolled into view */
  pickFromTable( id: string ): void {
    this.onBoard( { model: id, field: this.field, range: this.range, query: this.query, source: 'model' } );
    this.scrollToSection( 'top' );
  }

  /** every model, its change over each range read from one year of days */
  private buildCompare(): void {
    if ( !this.prices ) { return; }
    const byModel: { [ id: string ]: AiPriceEvent[] } = {};
    ( this.changes?.events || [] ).forEach( e => ( byModel[ e.modelId ] = byModel[ e.modelId ] || [] ).push( e ) );
    const ctx = { field: this.field, firstDay: this.indexRows.length ? this.indexRows[ 0 ].day : '' };
    this.compareAll = this.prices.models.slice().sort( aiWatchOrder ).map( m => {
      const year = aiWatchDays( m, byModel[ m.id ] || [], 365, ctx );
      const now = m[ this.field ];
      const changes = AI_WATCH_RANGES.map( r => aiWatchPct( now, year.slice( -aiWatchRangeDays( r.key ) ), 1 ) );
      return { id: m.id, name: m.name, vendor: m.vendor, platform: m.platform, input: m.input, output: m.output, price: now, changes };
    } );
    const seen: { [ key: string ]: boolean } = {};
    this.platformKeys = this.compareAll.map( r => r.platform ).filter( k => ( seen[ k ] ? false : ( seen[ k ] = true ) ) );
    this.sortTable();
  }

  private sortTable(): void {
    const q = this.tableQuery.trim().toLowerCase();
    const key = this.sortKey;
    const value = ( r: CompareRow ): number | string | null =>
      key === 'name' ? r.name : key === 'platform' ? null : key === 'price' ? r.price : r.changes[ key ];
    const rows = this.compareAll.filter( r => ( this.tablePlatform === 'all' || r.platform === this.tablePlatform )
      && ( !q || `${ r.name } ${ r.vendor } ${ r.id } ${ this.platformLabel( r.platform ) }`.toLowerCase().indexOf( q ) >= 0 ) );
    if ( key !== 'platform' ) {
      rows.sort( ( a, b ) => {
        const x = value( a ), y = value( b );
        if ( x === null || y === null ) { return x === y ? 0 : x === null ? 1 : -1; }
        const order = typeof x === 'string' ? x.localeCompare( y as string ) : x - ( y as number );
        return this.sortDesc ? -order : order;
      } );
    } else if ( this.sortDesc ) {
      rows.reverse();
    }
    this.compareRows = rows;
  }

  // ---------------------------------------------------------------- the chips

  scrollToSection( key: string ): void {
    const scroller = ( document.body.scrollHeight > document.body.clientHeight + 4 ) ? document.body : document.documentElement;
    if ( key === 'top' ) {
      this.activeSection = 'top';
      this.tweenScroll( scroller, 0 );
      return;
    }
    const el = document.getElementById( 'sec-' + key );
    if ( !el ) { return; }
    this.activeSection = key;
    let headerOffset = 84;
    const bar = document.querySelector( '.cp-chipbar' ) as HTMLElement | null;
    if ( bar ) {
      const stickyTop = parseFloat( getComputedStyle( bar ).top ) || 72;
      headerOffset = stickyTop + bar.offsetHeight + 16;
    }
    this.tweenScroll( scroller, Math.max( 0, el.getBoundingClientRect().top + scroller.scrollTop - headerOffset ) );
  }

  private tweenScroll( scroller: HTMLElement, target: number ): void {
    const startTop = scroller.scrollTop;
    const start = performance.now();
    const ease = ( t: number ) => 1 - Math.pow( 1 - t, 3 );
    const step = ( now: number ) => {
      const p = Math.min( 1, ( now - start ) / 420 );
      scroller.scrollTop = startTop + ( target - startTop ) * ease( p );
      if ( p < 1 ) { requestAnimationFrame( step ); }
    };
    requestAnimationFrame( step );
  }
}
